import type { BugReviewItem } from '@sr-test-wizard/shared';
import { JiraKeyLink } from '../../components/JiraKeyLink';

interface Props {
  bug: BugReviewItem;
  active: boolean;
  onSelect: () => void;
}

function statusBadgeClass(category: string | null, isDone: boolean): string {
  if (isDone) return 'good';
  const c = (category ?? '').toLowerCase();
  if (c === 'in progress') return 'info';
  if (c === 'to do') return 'warn';
  return 'grey';
}

/**
 * Single row in the Bugs panel left rail. Click selects the bug and pulls the
 * detail / chat into the right pane. The status badge colour mirrors Jira's
 * status category so a glance at the rail tells you which bugs are still
 * open vs already done.
 */
export function BugCard({ bug, active, onSelect }: Props) {
  return (
    <button
      type="button"
      className={`bug-card${active ? ' bug-card-active' : ''}`}
      onClick={onSelect}
    >
      <div className="bug-card-row">
        <JiraKeyLink keyId={bug.key} className="badge mono small" />
        <span className={`badge ${statusBadgeClass(bug.statusCategory, bug.isDone)} small`}>
          {bug.status ?? 'Unknown'}
        </span>
        {bug.priority && (
          <span className="badge small" title="Priority">
            {bug.priority}
          </span>
        )}
        <span className="spacer" />
        {bug.commentCount > 0 && (
          <span className="muted small" title="Comments">
            {bug.commentCount} c
          </span>
        )}
        {bug.attachmentCount > 0 && (
          <span className="muted small" title="Attachments">
            {bug.attachmentCount} a
          </span>
        )}
      </div>
      <div className="bug-card-summary" title={bug.summary}>
        {bug.summary || '(no summary)'}
      </div>
      <div className="bug-card-row muted small">
        {bug.linkedTestKey && <span>from {bug.linkedTestKey}</span>}
        {bug.assignee && <span>{bug.assignee}</span>}
        {bug.updatedAt && <span>{bug.updatedAt.slice(0, 10)}</span>}
      </div>
    </button>
  );
}
